import type { MemoryDraft } from "./memory-drafts";
import {
  createSettingsChanges,
  toEditableSettings,
  type EditableMemoryState,
  type EditableSettings,
} from "./settings-form";
import type { SettingsChanges, SettingsSnapshot } from "./types";

export type SettingsSaveSection = "persona" | "system_prompt" | "memory" | "style" | "image" | "model" | "title";

export interface SettingsDraftState {
  values: EditableSettings;
  memories: EditableMemoryState[];
  drafts: MemoryDraft[];
}

export interface SettingsSaveResult {
  changes: SettingsChanges;
  section: SettingsSaveSection;
  snapshot: SettingsSnapshot;
  submitted: SettingsDraftState;
  skipped: boolean;
}

const SECTION_FIELDS: Record<Exclude<SettingsSaveSection, "memory">, Array<keyof EditableSettings>> = {
  persona: ["selected_persona"],
  system_prompt: ["system_prompt"],
  style: ["style_guide"],
  image: ["image_cfg", "image_sampler", "image_scheduler", "image_resolution"],
  model: ["image_model"],
  title: ["title", "title_enabled"],
};

function savedMemories(snapshot: SettingsSnapshot): EditableMemoryState[] {
  return snapshot.memories.map((memory) => ({
    ...memory,
    deleted: false,
    originalMemory: memory.memory,
    originalPermanent: memory.permanent,
  }));
}

export function isSectionDirty(saved: SettingsSnapshot, draft: SettingsDraftState, section: SettingsSaveSection): boolean {
  if (section === "memory") {
    return draft.memories.some((memory) => memory.deleted
      || memory.memory !== memory.originalMemory
      || memory.permanent !== memory.originalPermanent)
      || draft.drafts.some((memory) => memory.content.trim().length > 0);
  }
  return SECTION_FIELDS[section].some((field) => draft.values[field] !== saved[field]);
}

/** 用目标栏目的有效草稿生成稳定签名；无关栏目编辑不会改变幂等重试 identity。 */
export function sectionDraftSignature(section: SettingsSaveSection, draft: SettingsDraftState): string {
  if (section === "memory") {
    return JSON.stringify({
      memories: draft.memories.map(({ id, memory, permanent, deleted, originalMemory, originalPermanent }) => ({
        id, memory, permanent, deleted, originalMemory, originalPermanent,
      })),
      drafts: draft.drafts.map(({ content, permanent }) => ({ content, permanent })),
    });
  }
  return JSON.stringify(SECTION_FIELDS[section].map((field) => [field, draft.values[field]]));
}

/**
 * Settings PUT 是全量协议。这里只把目标栏目的草稿覆盖到最近一次服务端快照上，
 * 其余字段和记忆操作均使用已保存基线，避免顺带提交别的栏目草稿。
 */
export function createSectionSettingsChanges(
  saved: SettingsSnapshot,
  draft: SettingsDraftState,
  section: SettingsSaveSection,
  requestId: string,
): SettingsChanges {
  const values = toEditableSettings(saved);
  if (section !== "memory") {
    for (const field of SECTION_FIELDS[section]) {
      // 每个字段在 EditableSettings 中保持原类型；循环赋值需要在这个受控边界收窄。
      (values as unknown as Record<keyof EditableSettings, string | boolean>)[field] = draft.values[field];
    }
  }
  return createSettingsChanges(
    saved,
    values,
    section === "memory" ? draft.memories : savedMemories(saved),
    section === "memory" ? draft.drafts : [],
    requestId,
  );
}

/** 只把本次提交且此后没有继续编辑的字段推进到服务端返回值。 */
export function mergeSectionSnapshotIntoValues(
  current: EditableSettings,
  submitted: EditableSettings,
  snapshot: SettingsSnapshot,
  section: Exclude<SettingsSaveSection, "memory">,
): EditableSettings {
  const next = { ...current };
  for (const field of SECTION_FIELDS[section]) {
    if (current[field] === submitted[field]) {
      (next as unknown as Record<keyof EditableSettings, string | boolean>)[field] = snapshot[field];
    }
  }
  return next;
}

/**
 * 所有普通 Settings PUT 共用一个串行队列。每个任务真正执行时才读取最新 revision，
 * 因此连续自动保存不会互相制造 CAS 冲突；失败只拒绝当前任务，不阻断后续任务。
 */
export class SettingsSaveController {
  private tail: Promise<void> = Promise.resolve();
  private saved: SettingsSnapshot;

  constructor(
    initial: SettingsSnapshot,
    private readonly saveRequest: (changes: SettingsChanges) => Promise<SettingsSnapshot>,
  ) {
    this.saved = initial;
  }

  replaceSnapshot(snapshot: SettingsSnapshot): void {
    this.saved = snapshot;
  }

  save(section: SettingsSaveSection, submitted: SettingsDraftState, requestId: string = crypto.randomUUID()): Promise<SettingsSaveResult> {
    const operation = this.tail.then(async () => {
      const changes = createSectionSettingsChanges(this.saved, submitted, section, requestId);
      if (!isSectionDirty(this.saved, submitted, section)) {
        return { changes, section, snapshot: this.saved, submitted, skipped: true };
      }
      const snapshot = await this.saveRequest(changes);
      this.saved = snapshot;
      return { changes, section, snapshot, submitted, skipped: false };
    });
    this.tail = operation.then(() => undefined, () => undefined);
    return operation;
  }
}
